import { Row } from 'react-bootstrap';
import {
  ExcelExport,
  ExcelExportColumn
} from '@progress/kendo-react-excel-export';
import { useAuth } from 'react-oidc-context';
import { useParams } from 'react-router-dom';
import { HubConnection } from '@microsoft/signalr';
import { Input } from '@progress/kendo-react-inputs';
import { Button } from '@progress/kendo-react-buttons';
import React, { useContext, useEffect, useMemo, useState } from 'react';

import {
  getTeamList,
  getTeamStateAsync,
  joinTeam,
  leaveTeam
} from '../../services/teams';
import {
  ChatFromFacilitator,
  PostChatFromFacilitator,
  getPlayerState,
  submitTaskAnswerScoringAsync
} from '../../services/players';
import {
  getGroupList,
  getGroupSelectedAsync,
  joinGroup,
  leaveGroup
} from '../../services/groups';
import {
  NotificationContent,
  NotificationContext
} from '../../contexts/notification';
import {
  GetTeamSelectedResponse,
  TeamResponse
} from '../../types/responses/team-response';
import { GameContext } from '../../contexts/game';
import Popup from '../../components/popup-window';
import { TeamMembers } from './teams/team-members';
import {
  GetForm,
  GetTaskContentById
} from '../../utils/game-document/assets/task-contents';
import { PlayerContextState } from '../../contexts/player';
import { ManualScoring } from '../../types/manual-scoring';
import { capitalizeFirstLetter } from '../../utils/string';
import { TaskContentAnswer } from '../../types/entities/task';
import { GetTaskById, GetTaskTitle } from '../../utils/game-document/assets';
import { downloadFilesAndZip } from '../../utils/file-utility';
import { PlayerState, TeamStateFull } from '../../types/state';
import { TaskContentAnswerEntity, TaskEntity } from '../../types';
import { GameDocumentContext } from '../../contexts/game-document';
import { postBroadcastGameStateAsync } from '../../services/games';
import { GroupResponse } from '../../types/responses/group-response';
import { TabButtonComponent } from '../../components/tabs/tab-button';
import { CreateTeam } from '../../components/facilitator/create-team';
import { PlayerResponse } from '../../types/responses/player-response';
import { TaskCard } from '../../components/facilitator/task/task-card';
import { GetResourceValue } from '../../utils/game-document/resources';
import { TaskState } from '../../components/facilitator/task/task-list';
import { useDownloadSelected } from '../../hooks/use-download-selected';
import { FacilitatorAndPlayerChat } from './chat/facilitator-player-chat';
import { TaskARCard } from '../../components/facilitator/task/task-AR-card';
import { AnswerType } from '../../types/game-document/entities/task-content';
import { FacilitatorState } from '../../components/facilitator/facilitator-state';
import { FileZipDownloadRequest } from '../../types/facilitator/file-zip-download';
import { TaskContentCard } from '../../components/facilitator/task/task-content-card';
import { AllTeamFacilitatorState } from '../../features/facilitator/team/all-team-state';
import { TaskDownloadWindow } from '../../features/facilitator/task/task-download-window';
import { TaskContentARCard } from '../../components/facilitator/task/task-content-AR-card';

export const TeamsPage = () => {
  const { gameCode } = useParams();
  const [game, , connection] = useContext(GameContext);
  const [gameDocument] = useContext(GameDocumentContext);
  const [notification, setNotification] = useContext(NotificationContext);
  const [teams, setTeams] = useState<TeamResponse[]>([]);
  const [groups, setGroups] = useState<GroupResponse[]>([]);

  const [searchTeam, setSearchTeam] = useState<string>('');
  const [teamState, setTeamState] = useState<TeamStateFull>();
  const [selectedTeam, setSelectedTeam] = useState<GetTeamSelectedResponse>();
  const [selectedGroup, setSelectedGroup] = useState<GetTeamSelectedResponse>();
  const [selectedGroupCode, setSelectedGroupCode] = useState<string>('');
  const [playerStates, setPlayerStates] = useState<PlayerContextState[]>([]);

  const [selectedPlayer, setSelectedPlayer] = useState<PlayerState>();
  const [showPlayerChat, setShowPlayerChat] = useState<boolean>(false);
  const [showTeamList, setShowTeamList] = useState(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [activeTab, setActiveTab] = useState<number>(0);
  const [downloadOptionsVisible, setDownloadOptionsVisible] =
    useState<boolean>(false);
  const tabs = ['Details', 'Members', 'Tasks'];
  const allTeamCode = 'all_teams';

  const taskList = (gameDocument.gameDocument?.assets.tasks ?? []).map(
    (t) => t.id
  );

  const {
    selectedDownload,
    onResetSelected,
    onDownloadSelected,
    onAddRemoveSelectedDownload
  } = useDownloadSelected(
    `${gameCode}-${selectedTeam?.name}-download-selected.zip`
  );

  const auth = useAuth();
  const userId = parseInt(auth.user?.profile.sub!);
  const userName = auth.user?.profile.name!;

  const gameStateListener = async (connection: HubConnection) => {
    connection.on('TeamCreated', async (teamCode: string) => {
      if (teamCode) {
        getAllTeams();
      }
    });
    connection.onclose((e) => {
      console.error('error');
    });
  };

  const getAllTeams = async () => {
    let teamsResult = (await getTeamList(gameCode ?? '')) as TeamResponse[];
    if (teamsResult.length > 0) {
      teamsResult.unshift({
        name: 'All Teams',
        teamCode: allTeamCode
      });
    }
    setTeams(teamsResult);

    setSelectedTeam(teamsResult[0]);
  };

  const getSelectedTeamStateAsync = async () => {
    if (selectedTeam && selectedTeam.teamCode !== allTeamCode) {
      setSelectedGroup({});
      setSelectedGroupCode('');
      setPlayerStates([]);
      let teamStateResponse = await getTeamStateAsync(
        gameCode ?? '',
        selectedTeam.teamCode!
      );
      teamStateResponse.createdDateUtc = selectedTeam.createdDateUtc;
      setTeamState(teamStateResponse);
      await defaultSelectedGroupHandler(selectedTeam);
      getPlayersStateAsync(selectedTeam.teamCode!);
    }
  };

  const getAllGroups = async () => {
    let groupsResult = (await getGroupList(gameCode ?? '')) as GroupResponse[];
    setGroups(groupsResult);
  };

  const getPlayersStateAsync = async (teamCode: string) => {
    const playerStatePromises = game?.gameState?.players
      ?.filter((x) => x.teamCode === teamCode)
      .map(async (player) => {
        return await getPlayerState(gameCode ?? '', player.code!);
      });
    const playerStates = await Promise.all(playerStatePromises!);
    setPlayerStates(playerStates);
  };

  const updatePlayerStateTaskScore = (scoring: ManualScoring) => {
    if (playerStates) {
      setPlayerStates((prevItemPlayerStates) => {
        return prevItemPlayerStates.map((item) => {
          if (item.playerState?.code === scoring.playerCode) {
            // Find the task
            const tasks = item.playerState?.tasks;
            const taskIndex = tasks?.findIndex((x) => x.id === scoring.taskId);

            if (taskIndex === -1 || taskIndex === undefined) return item; // Task not found

            // Find the form index
            const taskContentFormAnswers =
              tasks![taskIndex]?.taskContentFormAnswers;
            const formIndex = taskContentFormAnswers?.findIndex(
              (x) => x.formId === scoring.formId
            );

            if (formIndex === -1 || formIndex === undefined) return item; // Form not found

            // Update the score immutably
            let newPlayerStates = { ...item };
            let newTasks = [...newPlayerStates.playerState!.tasks!];
            let newFormAnswers = [
              ...newTasks[taskIndex].taskContentFormAnswers!
            ];

            newFormAnswers[formIndex] = {
              ...newFormAnswers[formIndex],
              score: scoring.score
            };

            newTasks[taskIndex] = {
              ...newTasks[taskIndex],
              taskContentFormAnswers: newFormAnswers
            };

            newPlayerStates = {
              ...newPlayerStates,
              playerState: {
                ...newPlayerStates.playerState!,
                tasks: newTasks
              }
            };

            return newPlayerStates;
          } else {
            return item;
          }
        });
      });
    }
  };

  /**
   * Handler when team selected
   * @param team
   */
  const syncTeamState = async (team: TeamResponse) => {
    setSelectedTeam(team);
    setSelectedGroup({});
    setSelectedGroupCode('');

    if (team.name === 'All Teams') return;

    try {
      let teamStateResponse = await getTeamStateAsync(
        gameCode ?? '',
        team?.teamCode!
      );
      teamStateResponse.createdDateUtc = team.createdDateUtc;
      await defaultSelectedGroupHandler(team);

      setTeamState(teamStateResponse);
    } catch (error) {
      console.error(error);
      setTeamState(undefined);
    }
  };

  const defaultSelectedGroupHandler = async (team: TeamResponse) => {
    if (
      team.groupCode &&
      team.groupCode !== '00000000-0000-0000-0000-000000000000'
    ) {
      let groupResult = await getGroupSelectedAsync(
        gameCode ?? '',
        team.groupCode ?? ''
      );
      if (groupResult) {
        setSelectedGroup(groupResult);
        setSelectedGroupCode(groupResult.code!);
      } else {
        setSelectedGroup({});
        setSelectedGroupCode('');
      }
    } else {
      setSelectedGroup({});
      setSelectedGroupCode('');
    }
  };

  const handleChangeGroup = (group: GroupResponse) => {
    if (group) {
      setSelectedGroupCode(group ? group.code! : '');
    } else {
      setSelectedGroupCode('');
    }
  };

  /**
   * Handler on team search clicked
   * @param search
   */
  const onSearcHandler = (search: string) => {
    setSearchTeam(search);
  };

  const onDisabledChatHandler = (teamCode: string, isDisabledChat: boolean) => {
    let newTeams = teams;

    let index = teams?.findIndex((x) => x.teamCode === teamCode);
    if (index > -1) {
      newTeams[index].isChatDisabled = isDisabledChat;

      setTeams(newTeams);
    }
  };

  const handleAddTeamToGroup = () => {
    joinGroup(selectedGroupCode!, selectedTeam?.teamCode!, gameCode ?? '')
      .then((response) => {
        if (response) {
          responseGroupNotification('Success add team to a group ');
          // getAllTeams();
        }
      })
      .catch((err) => {
        console.error(err);
      });
  };

  const handleRemoveTeamFromGroup = async () => {
    await leaveGroup(
      selectedGroup?.code!,
      selectedTeam?.teamCode!,
      gameCode ?? ''
    )
      .then((response) => {
        responseGroupNotification('Team has been removed from group ');
        // getAllTeams();
      })
      .catch((err) => {
        console.error(err);
      });
  };

  const responseGroupNotification = (message: string) => {
    const responseTeamNotification: NotificationContent = {
      icon: 'notifications',
      isHide: false,
      message: (
        <span>
          <strong>{message}</strong>
        </span>
      )
    };
    const content: NotificationContent[] = notification.content;
    content.push(responseTeamNotification);
    setNotification({ ...notification, content });
  };

  const changePlayerTeamHandler = async (
    leaveTeamCode: string,
    joinTeamCode: string,
    playerCode: string
  ) => {
    try {
      setIsLoading(true);
      await leaveTeam(leaveTeamCode, playerCode, gameCode ?? '');
      await joinTeam(joinTeamCode, playerCode, gameCode ?? '');
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
      setShowTeamList(false);
    }
  };

  const onSendMessageHandler = async (message: string) => {
    let payload: ChatFromFacilitator = {
      facilitatorName: userName,
      facilitatorUserProfileId: userId,
      toPlayerCode: (selectedPlayer as PlayerResponse).code!,
      message: message
    };

    await PostChatFromFacilitator(gameCode ?? '', payload);
  };

  const submitScoring = async (scoring: any) => {
    try {
      await submitTaskAnswerScoringAsync(scoring);
    } catch (error) {
      console.error('Failed submit scoring');
    } finally {
      updatePlayerStateTaskScore(scoring);
    }
  };

  const _exporter = React.createRef<ExcelExport>();

  const toggleDownloadOptions = () => {
    setDownloadOptionsVisible(!downloadOptionsVisible);
  };

  const exportTasks = (type: 'photos' | 'videos' | 'all') => {
    console.log('Downloading type:', type);
    switch (type) {
      case 'photos':
        photosExport();
        break;
      case 'videos':
        videosExport();
        break;
      default:
        excelExport();
        break;
    }
    toggleDownloadOptions();
  };

  const photosExport = () => exportByType('Image', 'photos');
  const videosExport = () => exportByType('Video', 'videos');

  const excelExport = () => {
    if (_exporter.current) {
      _exporter.current.save();
    }
  };

  const exportByType = (questionType: 'Image' | 'Video', suffix: string) => {
    const taskRows = generateTeamTaskAnswers('Task');
    const filesToDownload: FileZipDownloadRequest[] = taskRows
      .filter((task) => task.questionType === questionType && task.answer)
      .map((task) => ({
        containerFolder: '',
        fileUrl: task.answer as string
      }));

    if (filesToDownload.length > 0) {
      downloadFilesAndZip(
        filesToDownload,
        `${gameCode ?? ''}-${selectedTeam?.name}-All${suffix}.zip`
      );
    }
  };

  const generateTeamTaskAnswers = (
    taskFilterType: 'Task' | 'OnDemandTask'
  ): TaskState[] => {
    const result: TaskState[] = [];
    const teamPlayer = playerStates?.filter(
      (x) => x.playerState?.teamCode === selectedTeam!.teamCode
    );
    teamPlayer.forEach((playerState) => {
      playerState?.playerState?.tasks?.forEach((task) => {
        const shouldInclude =
          taskFilterType === 'Task'
            ? taskList.includes(task.id ?? '')
            : !taskList.includes(task.id ?? '');

        if (!shouldInclude) return;

        task?.taskContentFormAnswers?.forEach((taskContentAnswer) => {
          const taskById = GetTaskById(
            gameDocument.gameDocument,
            task.id || ''
          );
          if (!taskById) return;

          const form = GetForm(
            gameDocument.gameDocument!,
            taskById.taskContentId || '',
            taskContentAnswer.formId || ''
          );
          if (!form) return;

          const question = GetResourceValue(
            gameDocument.gameDocument!,
            form.questionResId || '',
            ''
          );
          const questionType = capitalizeFirstLetter(form.type || '');
          const scoringType = form.isManualScoring ? 'M' : 'A';
          const answer = buildAnswers(form.type, taskContentAnswer) || '';

          result.push({
            taskId: task.id || '',
            taskName:
            GetTaskTitle(gameDocument.gameDocument, task.id || '') ||
            taskById.name ||
            '',
            question,
            playerName: playerState?.playerState?.name || '',
            playerCode: playerState?.playerState?.code || '',
            questionType,
            scoringType,
            formId: taskContentAnswer.formId || '',
            score: parseInt(taskContentAnswer?.score?.toString() ?? '0'),
            taskContentId: taskById.taskContentId || '',
            answer
          });
        });
      });
    });

    return result;
  };

  const buildAnswers = (
    answerType: AnswerType,
    taskContentAnswer: TaskContentAnswer
  ) => {
    if (answerType === 'checkbox' || answerType === 'radio') {
      return taskContentAnswer?.answers
        ?.map((x) => {
          {
            return GetResourceValue(
              gameDocument?.gameDocument!,
              x.answerResId,
              ''
            );
          }
        })
        ?.join(', ');
    } else {
      return taskContentAnswer?.answers?.map((x) => x.value).join(', ');
    }
  };

  const selectedTeamFromGameState = useMemo(() => {
    return game.gameState?.teams?.find((t) => t.code === selectedTeam?.teamCode);
  }, [game.gameState?.teams, selectedTeam?.teamCode]);

  useEffect(() => {
    getAllGroups();
    getAllTeams();
  }, []);

  useEffect(() => {
    getSelectedTeamStateAsync();
  }, [selectedTeam]);

  useEffect(() => {
    if (!connection) return;
    gameStateListener(connection);
  }, [connection]);

  return (
    <div
      className={
        'd-flex flex-column flex-grow-1 h-100 w-100 overflow-hidden gap-2'
      }>
      {isLoading && (
        <div
          className={
            'd-flex flex-column align-items-center justify-content-center h-100 w-100 gap-2'
          }>
          <span className={'loader'}></span>
          <label className={'text-gray'}>Loading...</label>
        </div>
      )}
      {teams.length === 0 && <h3>No teams found</h3>}
      <div className={'d-flex justify-content-between mb-3'}>
        <div className={'w-25'}>
          <Input
            id={'search-text'}
            type={'text'}
            className={'form-control'}
            placeholder={'Search team...'}
            onChange={(e) => onSearcHandler((e.target.value as string) ?? '')}
          />
        </div>
        <CreateTeam onSubmitClick={getAllTeams} />
      </div>

      <div className={'d-flex h-100 flex-grow-1 overflow-hidden'}>
        <div
          className={
            'd-flex flex-column h-100 overflow-auto group-menu border-list'
          }>
          <ul className="list-group facilitator-border-none">
            {teams
              .filter((x) =>
                !searchTeam || searchTeam === ''
                  ? x
                  : x.name?.toLowerCase()?.includes(searchTeam?.toLowerCase())
              )
              .map((item) => {
                return (
                  <li
                    className="list-group-item cursor-pointer"
                    onClick={async () => syncTeamState(item)}>
                    <div className={'d-flex justify-content-between'}>
                      <small
                        className={`${
                          item.teamCode === selectedTeam?.teamCode
                            ? 'fw-bold text-dark'
                            : 'text-dark'
                        }`}>
                        {item?.name}
                      </small>
                      <small className={'text-dark'}>
                        {item.teamCode === allTeamCode
                          ? teams.length - 1
                          : game?.gameState?.players?.filter(
                              (x) => x.teamCode === item?.teamCode
                            )?.length}
                      </small>
                    </div>
                  </li>
                );
              })}
          </ul>
        </div>
        <div className={'team-content'}>
          {selectedTeam && selectedTeam.teamCode === allTeamCode && (
            <Row className={'ml-3'}>
              <Row className={'container-tab-component'}>
                <TabButtonComponent
                  thisTabIndex={-1}
                  activeTab={-1}
                  title={'Details'}
                  onSetActiveTab={(tabIndex) => {}}
                />
              </Row>
              <Row>
                <AllTeamFacilitatorState
                  isDisableConfirm={
                    game.gameState?.status === 'Finished' ||
                    !game.activityState?.hasGame
                  }
                />
              </Row>
            </Row>
          )}
          {selectedTeam && selectedTeam.teamCode !== allTeamCode && (
            <Row className={'ml-3'}>
              <Row className={'container-tab-component '}>
                {tabs.map((item, index) => (
                  <TabButtonComponent
                    key={item}
                    thisTabIndex={index}
                    activeTab={activeTab}
                    title={item}
                    onSetActiveTab={(tabIndex) => {
                      setActiveTab(tabIndex);
                    }}
                  />
                ))}
                <div
                  onClick={async () =>
                    await postBroadcastGameStateAsync(gameCode)
                  }
                  className={
                    'd-flex justify-content-between align-items-center text-info cursor-pointer w-25'
                  }>
                  <span className="material-symbols-outlined">sync</span>
                  {activeTab === 2 && (
                    <div className={'d-flex gap-2'}>
                      <Button
                        themeColor={'success'}
                        onClick={toggleDownloadOptions}>
                        Download
                      </Button>
                      {selectedDownload.length > 0 && (
                        <Button
                          themeColor={'success'}
                          onClick={onDownloadSelected}>
                          Download Selected
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              </Row>
              <hr className={'mt-0'} />
              <Row>
                {activeTab === 0 && (
                  <FacilitatorState
                    isGroup={false}
                    isPlayer={false}
                    playerOrTeamCode={selectedTeam?.teamCode!}
                    selectedTeam={selectedTeam}
                    state={teamState}
                    groupList={groups}
                    nameState={selectedTeam?.name}
                    isLocked={selectedTeamFromGameState?.isLocked}
                    isDisabledChat={teamState?.isChatDisabled!}
                    onDisabledChatTeam={onDisabledChatHandler}
                    groupCode={selectedGroupCode}
                    handleChangeGroup={handleChangeGroup}
                    selectedGroup={selectedGroup}
                    handleAddTeamToGroup={handleAddTeamToGroup}
                    handleRemoveTeamFromGroup={handleRemoveTeamFromGroup}
                    isDisableConfirm={
                      game.gameState?.status === 'Finished' ||
                      !game.activityState?.hasGame
                    }
                  />
                )}
                {activeTab === 1 && (
                  <TeamMembers
                    selectedTeamCode={selectedTeam?.teamCode ?? ''}
                    setShowTeamList={setShowTeamList}
                    setSelectedPlayer={setSelectedPlayer}
                    setShowPlayerChat={setShowPlayerChat}
                  />
                )}
                {activeTab === 2 && (
                  <Row>
                    {playerStates
                      ?.filter(
                        (x) => x.playerState?.teamCode === selectedTeam.teamCode
                      )
                      .map((playerState, outerIndex) => {
                        return playerState?.playerState?.tasks?.map(
                          (task: TaskEntity, taskIndex: number) => {
                            if (task.dataARMiniGame) {
                              return taskList.includes(task.id ?? '') ? (
                                <TaskARCard
                                  key={`task-ar-card-${playerState?.playerState?.code}-${task.id}`}
                                  index={taskIndex}
                                  dataARMiniGame={task.dataARMiniGame}
                                  selectedTask={GetTaskById(
                                    gameDocument.gameDocument,
                                    task.id ?? ''
                                  )}
                                  playerName={playerState?.playerState?.name}
                                  playerCode={playerState?.playerState?.code}
                                  isEdit={false}
                                  onSubmitScore={submitScoring}
                                />
                              ) : (
                                <TaskContentARCard
                                  key={`task-content-ar-card-${playerState?.playerState?.code}-${task.id}`}
                                  index={taskIndex}
                                  dataARMiniGame={task.dataARMiniGame}
                                  selectedTaskContent={GetTaskContentById(
                                    gameDocument.gameDocument,
                                    task.id ?? ''
                                  )}
                                  playerName={playerState?.playerState?.name}
                                  playerCode={playerState?.playerState?.code}
                                  isEdit={false}
                                  onSubmitScore={submitScoring}
                                />
                              );
                            }

                            return task?.taskContentFormAnswers?.map(
                              (
                                taskContentAnswer: TaskContentAnswerEntity,
                                taskContentAnswerIndex: number
                              ) => {
                                return taskList.includes(task.id ?? '') ? (
                                  <TaskCard
                                    key={`task-card-${playerState?.playerState?.code}-${task.id}-${taskContentAnswer.formId}`}
                                    taskContentAnswer={taskContentAnswer}
                                    index={taskContentAnswerIndex}
                                    selectedTask={GetTaskById(
                                      gameDocument.gameDocument,
                                      task.id ?? ''
                                    )}
                                    playerName={playerState?.playerState?.name}
                                    playerCode={playerState?.playerState?.code}
                                    isEdit={false}
                                    onSubmitScore={submitScoring}
                                    selectedDownload={selectedDownload}
                                    onAddRemoveSelectedDownload={
                                      onAddRemoveSelectedDownload
                                    }
                                  />
                                ) : (
                                  <TaskContentCard
                                    key={`task-content-card-${playerState?.playerState?.code}-${task.id}-${taskContentAnswer.formId}`}
                                    taskContentAnswer={taskContentAnswer!}
                                    index={taskContentAnswerIndex}
                                    selectedTaskContent={GetTaskContentById(
                                      gameDocument.gameDocument,
                                      task.id ?? ''
                                    )}
                                    playerName={playerState?.playerState?.name}
                                    playerCode={playerState?.playerState?.code}
                                    isEdit={false}
                                    onSubmitScore={submitScoring}
                                    selectedDownload={selectedDownload}
                                    onAddRemoveSelectedDownload={
                                      onAddRemoveSelectedDownload
                                    }
                                  />
                                );
                              }
                            );
                          }
                        );
                      })}
                  </Row>
                )}
              </Row>
            </Row>
          )}
        </div>
        {showPlayerChat && selectedPlayer && (
          <Popup
            className={'box d-flex flex-column gap-3 w-50'}
            handleClose={() => setShowPlayerChat(false)}>
            <FacilitatorAndPlayerChat
              item={{
                id: selectedPlayer.code ?? '',
                name: selectedPlayer.name ?? '',
                type: 'player-chat'
              }}
              onSendMessage={(message) => onSendMessageHandler(message)}
            />
          </Popup>
        )}
        {showTeamList && (
          <Popup
            handleClose={() => {
              setSelectedPlayer(undefined);
              setShowTeamList(false);
            }}
            title={'Team list'}
            className={
              'box box-small assessment-export d-flex flex-column position-relative'
            }>
            <ul className={'p-0'}>
              {selectedPlayer &&
                teams &&
                teams
                  .filter((item) => item.teamCode !== selectedTeam?.teamCode)
                  .map((item, index) => {
                    return (
                      <li className={'k-listview-item row p-2 border-bottom'}>
                        <div
                          className={
                            'd-flex justify-content-between align-items-center'
                          }>
                          <div>{item.name}</div>
                          <Button
                            onClick={() => {
                              if (
                                selectedPlayer &&
                                selectedPlayer.code &&
                                selectedTeam &&
                                selectedTeam.teamCode
                              ) {
                                changePlayerTeamHandler(
                                  selectedTeam?.teamCode,
                                  item.teamCode!,
                                  selectedPlayer.code
                                );
                              }
                            }}
                            themeColor={'success'}>
                            Select team
                          </Button>
                        </div>
                      </li>
                    );
                  })}
            </ul>
          </Popup>
        )}
      </div>

      <ExcelExport
        data={generateTeamTaskAnswers('Task')}
        collapsible={true}
        fileName={`${gameCode ?? ''}-${selectedTeam?.name}-AllTasks.xlsx`}
        ref={_exporter}>
        <ExcelExportColumn field="taskName" title="Task" width={200} />
        <ExcelExportColumn field="playerName" title="Player" width={200} />
        <ExcelExportColumn
          field="questionType"
          title="Question type"
          width={200}
        />
        <ExcelExportColumn field="scoringType" title="Type" width={100} />
        <ExcelExportColumn field="answer" title="Answer" width={500} />
        <ExcelExportColumn field="score" title="Score" width={200} />
      </ExcelExport>

      {downloadOptionsVisible && (
        <TaskDownloadWindow
          toggleDialog={toggleDownloadOptions}
          onDownload={exportTasks}
        />
      )}
    </div>
  );
};
